import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Crown, Zap } from 'lucide-react';
import GlassCard from './GlassCard';
import { packages } from '../data/packages';

const Packages = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const [selectedCarType, setSelectedCarType] = useState('sedan');

  const content = {
    title: isAr ? 'باقاتنا المتميزة' : 'Premium Packages',
    subtitle: isAr
      ? 'اختر من بين باقاتنا المصممة بعناية لتناسب جميع احتياجاتك وميزانيتك مع سوبر كلاسيكي'
      : 'Choose from our carefully crafted packages for every need and budget with Super Classy',
    carTypes: {
      sedan: isAr ? 'سيدان' : 'SEDAN',
      suv: isAr ? 'جيب / SUV' : 'SUV',
      '7-seater': isAr ? '٧ ركاب' : '7-SEATER',
    },
    popular: isAr ? 'الأكثر طلباً' : 'Popular',
    startingPrice: isAr ? 'سعر البداية' : 'Starting Price',
    currency: isAr ? 'د.ك' : 'KD',
    bookBtn: isAr ? 'حجز باقة' : 'Book',
  };

  const openWhatsApp = (packageName) => {
    const carTypeLabel = content.carTypes[selectedCarType];
    const message = encodeURIComponent(
      isAr
        ? `مرحباً، أود حجز ${packageName} لسيارة ${carTypeLabel}.`
        : `Hi, I want to book ${packageName} package for ${selectedCarType}.`,
    );
    window.open(`https://wa.me/96550040026?text=${message}`, '_blank');
  };

  return (
    <section
      id='packages'
      className='section-padding py-20 from-gray-50 to-white dark:from-gray-900 dark:to-black'
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className='max-w-7xl mx-auto'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className='text-center mb-12'
        >
          <h2 className='text-4xl md:text-5xl font-black mb-4 text-gradient'>
            {content.title}
          </h2>
          <p className='text-xl text-gray-400 max-w-2xl mx-auto'>
            {content.subtitle}
          </p>
        </motion.div>

        {/* Car Type Selector */}
        <div className='flex justify-center mb-12'>
          <div className='inline-flex p-1.5 glass-card rounded-full shadow-inner border border-white/5'>
            {['sedan', 'suv', '7-seater'].map((type) => (
              <button
                key={type}
                onClick={() => setSelectedCarType(type)}
                className={`px-8 py-2.5 rounded-full font-bold text-sm transition-all duration-300 ${
                  selectedCarType === type
                    ? 'premium-gradient text-white shadow-lg scale-105'
                    : 'text-gray-500 hover:text-royal-purple-400'
                }`}
              >
                {content.carTypes[type]}
              </button>
            ))}
          </div>
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
          {packages.map((pkg, index) => {
            const price = pkg.prices[selectedCarType];
            const Icon = index === 1 ? Crown : index === 2 ? Star : Zap;
            const pName = isAr ? pkg.nameAr || pkg.name : pkg.name;
            const pDesc = isAr
              ? pkg.descriptionAr || pkg.description
              : pkg.description;
            const pFeatures = isAr
              ? pkg.featuresAr || pkg.features
              : pkg.features;

            return (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className='relative'
              >
                <GlassCard
                  className={`h-full p-8 flex flex-col border border-white/10 group ${
                    pkg.popular
                      ? 'border-2 border-royal-purple-500 shadow-2xl'
                      : ''
                  }`}
                >
                  <div
                    className={`flex items-center justify-between mb-8 ${isAr ? 'flex-row-reverse' : ''}`}
                  >
                    <div className='p-4 premium-gradient rounded-2xl shadow-xl'>
                      <Icon className='h-6 w-6 text-white' />
                    </div>
                    {pkg.popular && (
                      <span className='px-4 py-1.5 bg-royal-purple-500/20 text-royal-purple-400 font-black rounded-full text-[10px] uppercase tracking-widest'>
                        {content.popular}
                      </span>
                    )}
                  </div>

                  <h3 className='text-2xl font-black mb-2'>{pName}</h3>
                  <p className='text-gray-400 text-sm mb-8 font-medium'>
                    {pDesc}
                  </p>

                  {/* High-Impact Price Section as seen in your 2nd image */}
                  <div className='mb-8'>
                    <div
                      className={`flex items-baseline gap-2 ${isAr ? 'flex-row-reverse' : ''}`}
                    >
                      <span className='text-6xl font-black text-white'>
                        {price}
                      </span>
                      <span className='text-xl font-bold text-gray-400'>
                        {content.currency}
                      </span>
                      <span className='text-xs text-gray-500 font-bold'>
                        / {content.carTypes[selectedCarType]}
                      </span>
                    </div>
                    <p className='text-[10px] font-black text-gray-500 uppercase tracking-[0.2em] mt-2'>
                      {content.startingPrice}
                    </p>
                  </div>

                  <div className='space-y-4 mb-10 flex-grow'>
                    {pFeatures.map((feature, idx) => (
                      <div
                        key={idx}
                        className={`flex items-start gap-3 ${isAr ? 'flex-row-reverse text-right' : ''}`}
                      >
                        <div className='mt-1 p-0.5 bg-green-500/20 rounded-full flex-shrink-0'>
                          <Check className='h-3.5 w-3.5 text-green-500' />
                        </div>
                        <span className='text-gray-300 font-medium text-sm leading-tight'>
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => openWhatsApp(pName)}
                    className={`w-full py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-all duration-300 active:scale-95 ${
                      pkg.popular
                        ? 'premium-gradient text-white shadow-xl shadow-royal-purple-500/20'
                        : 'bg-white/5 hover:bg-white/10 text-white border border-white/10'
                    }`}
                  >
                    {content.bookBtn} {pName}
                  </button>
                </GlassCard>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Packages;
